import { CartUI, FulfillmentGroupsItemUI, ItemUI, OrderUI, PaymentMethod } from '@libs/common-node';
import { LoyaltyMemberPoints } from '@libs/common-types';
import { PaymentMethodsType } from '@libs/core';

import { buildFulfillmentMethodLabel } from '../../../analytics/src/helpers/buildFulfillmentMethodLabel';
import { IdentityUser, UserData } from '../providers';
import type { SubscriptionItem, SubscriptionOrder } from '../types/subscriptions';
import type {
    MedalliaContextExtras,
    MedalliaProductAccumulator,
    MedalliaProductRow,
    MedalliaSubscriptionProductAccumulator,
    MedalliaSubscriptionProductRow,
    OptimizelyActiveCampaign,
    PetsmartMedalliaContext,
    PetsmartMedalliaOrderInfo,
    PetsmartMedalliaProduct,
    PetsmartMedalliaSubscriptionActionInfo,
    PetsmartMedalliaSubscriptionInfo,
    PetsmartMedalliaUserInfo,
} from './types';

const MEDALLIA_DELIMITER = ' | ';
const MEDALLIA_NULL_VALUE = 'null';

const toStringValue = (value: unknown): string | undefined => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    return value.toString();
};

const toSerializedStringArray = (values: string[]): string | undefined => {
    const filteredValues = values.filter(Boolean);

    if (!filteredValues.length) {
        return undefined;
    }

    return JSON.stringify(filteredValues);
};

// Only writes to the target when the value is a non-empty string. Omits keys
// entirely rather than setting null/undefined/empty, per Medallia's requirement
// that absent data produces no key at all in the context object.
const addIfAvailable = <T extends object, K extends keyof T>(target: T, key: K, value: unknown): void => {
    const stringValue = toStringValue(value);

    if (stringValue) {
        target[key] = stringValue as T[K];
    }
};

const getCustomerName = (cart: CartUI, extras?: MedalliaContextExtras): string | undefined => {
    if (extras?.profileName) {
        return extras.profileName;
    }

    if (cart.pickupPersonInformation?.fullName) {
        return cart.pickupPersonInformation.fullName;
    }

    const firstName = cart.shippingAddress?.firstName;
    const lastName = cart.shippingAddress?.lastName;
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    return fullName || undefined;
};

const getCustomerPhone = (cart: CartUI, extras?: MedalliaContextExtras): string | undefined => {
    if (extras?.profilePhone) {
        return extras.profilePhone;
    }

    return cart.pickupPersonInformation?.phoneNumber ?? cart.shippingAddress?.phone;
};

const getCustomerEmail = (cart: CartUI, extras?: MedalliaContextExtras): string | undefined => {
    return extras?.profileEmail ?? cart.customerEmail;
};

const buildUserInfo = (cart: CartUI, extras?: MedalliaContextExtras): PetsmartMedalliaUserInfo => {
    const userInfo: PetsmartMedalliaUserInfo = {};

    addIfAvailable(userInfo, 'customerId', extras?.customerId);
    addIfAvailable(userInfo, 'loyaltyId', extras?.loyaltyId);
    addIfAvailable(userInfo, 'loyaltyTier', extras?.loyaltyTier);
    addIfAvailable(userInfo, 'customerName', getCustomerName(cart, extras));
    addIfAvailable(userInfo, 'customerPhone', getCustomerPhone(cart, extras));
    addIfAvailable(userInfo, 'customerEmail', getCustomerEmail(cart, extras));

    return userInfo;
};

const sanitizeMedalliaValue = (value: unknown): string => {
    if (value === undefined || value === null) {
        return MEDALLIA_NULL_VALUE;
    }

    const stringValue = toStringValue(value);

    if (!stringValue) {
        return MEDALLIA_NULL_VALUE;
    }

    return stringValue.replaceAll('|', '/');
};

const getProductUnitPrice = (item: ItemUI): string | undefined => {
    if (item.basePriceWithDiscounts == null || !item.quantity) {
        return undefined;
    }

    return (item.basePriceWithDiscounts / 100 / item.quantity).toFixed(2);
};

const buildProduct = (item: ItemUI, fulfillmentGroup: FulfillmentGroupsItemUI): MedalliaProductRow => ({
    productSku: item.sku,
    productName: item.name,
    productBrand: item.merchandiseBrand,
    productCategory: item.mhVicePresidentName,
    productPrice: getProductUnitPrice(item),
    productQuantity: item.quantity,
    fulfillmentMethod: buildFulfillmentMethodLabel(fulfillmentGroup.fulfillmentMethod, item),
    bopisStoreName: fulfillmentGroup.storeName,
    bopisStoreId: fulfillmentGroup.storeId,
});

const createProductAccumulator = (): MedalliaProductAccumulator => ({
    productCount: 0,
    productSkus: [],
    productNames: [],
    productBrands: [],
    productCategories: [],
    productPrices: [],
    productQuantities: [],
    fulfillmentMethods: [],
    bopisStoreNames: [],
    bopisStoreIds: [],
});

const addProductToAccumulator = (
    accumulator: MedalliaProductAccumulator,
    product: MedalliaProductRow
): MedalliaProductAccumulator => {
    accumulator.productCount += 1;
    accumulator.productSkus.push(sanitizeMedalliaValue(product.productSku));
    accumulator.productNames.push(sanitizeMedalliaValue(product.productName));
    accumulator.productBrands.push(sanitizeMedalliaValue(product.productBrand));
    accumulator.productCategories.push(sanitizeMedalliaValue(product.productCategory));
    accumulator.productPrices.push(sanitizeMedalliaValue(product.productPrice));
    accumulator.productQuantities.push(sanitizeMedalliaValue(product.productQuantity));
    accumulator.fulfillmentMethods.push(sanitizeMedalliaValue(product.fulfillmentMethod));
    accumulator.bopisStoreNames.push(sanitizeMedalliaValue(product.bopisStoreName));
    accumulator.bopisStoreIds.push(sanitizeMedalliaValue(product.bopisStoreId));

    return accumulator;
};

const serializeProducts = (products: MedalliaProductAccumulator): PetsmartMedalliaProduct => ({
    productCount: String(products.productCount),
    productSkus: products.productSkus.join(MEDALLIA_DELIMITER),
    productNames: products.productNames.join(MEDALLIA_DELIMITER),
    productBrands: products.productBrands.join(MEDALLIA_DELIMITER),
    productCategories: products.productCategories.join(MEDALLIA_DELIMITER),
    productPrices: products.productPrices.join(MEDALLIA_DELIMITER),
    productQuantities: products.productQuantities.join(MEDALLIA_DELIMITER),
    fulfillmentMethods: products.fulfillmentMethods.join(MEDALLIA_DELIMITER),
    bopisStoreNames: products.bopisStoreNames.join(MEDALLIA_DELIMITER),
    bopisStoreIds: products.bopisStoreIds.join(MEDALLIA_DELIMITER),
});

const buildProducts = (cart: CartUI): PetsmartMedalliaProduct => {
    const products = cart.fulfillmentGroups.reduce<MedalliaProductAccumulator>(
        (accumulator, fulfillmentGroup) =>
            fulfillmentGroup.items.reduce<MedalliaProductAccumulator>(
                (productAccumulator, item) =>
                    addProductToAccumulator(productAccumulator, buildProduct(item, fulfillmentGroup)),
                accumulator
            ),
        createProductAccumulator()
    );

    return serializeProducts(products);
};

const buildPromoDiscountsApplied = (cart: CartUI): string | undefined => {
    return toSerializedStringArray([
        ...(cart.summary?.dealsAndOffers ?? []).map(deal => deal.offerName),
        ...(cart.promotions ?? []),
        ...(cart.discountCodes ?? []).map(discountCode => discountCode.code),
    ]);
};

const MEDALLIA_CREDIT_CARD_TYPES: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    mc: 'Mastercard',
    amex: 'AMEX',
    discover: 'Discover',
};

const mapMedalliaPaymentMethodLabel = (paymentMethod: PaymentMethod): string => {
    const { method, paymentCardType } = paymentMethod;

    if (method === PaymentMethodsType.CREDITCARD) {
        if (paymentCardType) {
            return MEDALLIA_CREDIT_CARD_TYPES[paymentCardType.toLowerCase()] ?? paymentCardType;
        }

        return PaymentMethodsType.CREDITCARD;
    }

    if (method === PaymentMethodsType.GIFTCARD) {
        return 'Gift Card';
    }

    return method;
};

const buildPaymentMethod = (cart: CartUI): string | undefined => {
    return toSerializedStringArray(
        (cart.paymentMethods ?? []).map(paymentMethod => mapMedalliaPaymentMethodLabel(paymentMethod))
    );
};

const buildOrderInfo = (cart: CartUI, extras?: MedalliaContextExtras): PetsmartMedalliaOrderInfo => {
    const orderInfo: PetsmartMedalliaOrderInfo = {};

    addIfAvailable(orderInfo, 'cartTotal', cart.summary?.totalAmount);
    addIfAvailable(orderInfo, 'promoDiscountsApplied', buildPromoDiscountsApplied(cart));
    addIfAvailable(orderInfo, 'paymentMethod', buildPaymentMethod(cart));
    addIfAvailable(
        orderInfo,
        'optimizelyCampaigns',
        extras?.optimizelyCampaigns?.length ? JSON.stringify(extras.optimizelyCampaigns) : undefined
    );

    return orderInfo;
};

const getProfileName = (userData?: UserData | null): string | undefined => {
    const firstName = userData?.profile?.firstName;
    const lastName = userData?.profile?.lastName;

    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    return fullName || undefined;
};

export const buildExtras = ({
    user,
    userData,
    memberPoints,
    optimizelyCampaigns,
}: {
    user?: IdentityUser;
    userData?: UserData | null;
    memberPoints?: LoyaltyMemberPoints | null;
    optimizelyCampaigns?: OptimizelyActiveCampaign[];
}): MedalliaContextExtras => {
    const extras: MedalliaContextExtras = {};

    addIfAvailable(extras, 'customerId', user?.customerprofileid?.toString());
    addIfAvailable(extras, 'loyaltyId', user?.loyaltyid?.toString());
    addIfAvailable(extras, 'loyaltyTier', memberPoints?.currentTierLevel?.toString());
    addIfAvailable(extras, 'profileName', getProfileName(userData));
    addIfAvailable(extras, 'profilePhone', userData?.profile?.phoneNumber?.toString());
    addIfAvailable(extras, 'profileEmail', userData?.profile?.emailAddress ?? user?.email);

    if (optimizelyCampaigns?.length) {
        extras.optimizelyCampaigns = optimizelyCampaigns;
    }

    return extras;
};

export const buildPetsmartMedalliaContextFromCartUI = (
    cart: CartUI,
    extras?: MedalliaContextExtras
): PetsmartMedalliaContext => {
    return {
        userInfo: buildUserInfo(cart, extras),
        products: buildProducts(cart),
        orderInfo: buildOrderInfo(cart, extras),
    };
};

const buildSubscriptionUserInfo = (extras?: MedalliaContextExtras): PetsmartMedalliaUserInfo => {
    const userInfo: PetsmartMedalliaUserInfo = {};

    addIfAvailable(userInfo, 'customerId', extras?.customerId);
    addIfAvailable(userInfo, 'loyaltyId', extras?.loyaltyId);
    addIfAvailable(userInfo, 'loyaltyTier', extras?.loyaltyTier);
    addIfAvailable(userInfo, 'customerName', extras?.profileName);
    addIfAvailable(userInfo, 'customerPhone', extras?.profilePhone);
    addIfAvailable(userInfo, 'customerEmail', extras?.profileEmail);

    return userInfo;
};

const getSubscriptionProductUnitPrice = (item: SubscriptionItem): string | undefined => {
    const price = toStringValue(item.price)?.trim();

    if (!price) {
        return undefined;
    }

    const numericPrice = Number(price);

    if (!Number.isFinite(numericPrice)) {
        return undefined;
    }

    return numericPrice.toFixed(2);
};

const buildSubscriptionProduct = (item: SubscriptionItem): MedalliaSubscriptionProductRow => ({
    productSku: item.product?.sku,
    productName: item.product?.name,

    // SubscriptionItem does not expose these values.
    // They will be serialized as the literal "null".
    productBrand: undefined,
    productCategory: undefined,

    productPrice: getSubscriptionProductUnitPrice(item),
    productTotalPrice: item.totalPrice,
    productQuantity: item.quantity,

    // SubscriptionItem does not expose fulfillment/BOPIS information.
    // One "null" is emitted for each subscription item.
    fulfillmentMethod: undefined,
    bopisStoreName: undefined,
    bopisStoreId: undefined,

    subscriptionId: item.subscription?.id,
    subscriptionType: item.subscription?.subscriptionType,
    subscriptionStartDate: item.subscription?.startDate,
    subscriptionCreated: item.subscription?.created,
    subscriptionUpdated: item.subscription?.updated,
    subscriptionFrequencyDays: item.subscription?.frequencyDays,
});

const createSubscriptionProductAccumulator = (): MedalliaSubscriptionProductAccumulator => ({
    ...createProductAccumulator(),
    productTotalPrices: [],
    subscriptionIds: [],
    subscriptionTypes: [],
    subscriptionStartDates: [],
    subscriptionCreatedDates: [],
    subscriptionUpdatedDates: [],
    subscriptionFrequencyDays: [],
});

const addSubscriptionProductToAccumulator = (
    accumulator: MedalliaSubscriptionProductAccumulator,
    product: MedalliaSubscriptionProductRow
): MedalliaSubscriptionProductAccumulator => {
    addProductToAccumulator(accumulator, product);

    accumulator.productTotalPrices.push(sanitizeMedalliaValue(product.productTotalPrice));
    accumulator.subscriptionIds.push(sanitizeMedalliaValue(product.subscriptionId));
    accumulator.subscriptionTypes.push(sanitizeMedalliaValue(product.subscriptionType));
    accumulator.subscriptionStartDates.push(sanitizeMedalliaValue(product.subscriptionStartDate));
    accumulator.subscriptionCreatedDates.push(sanitizeMedalliaValue(product.subscriptionCreated));
    accumulator.subscriptionUpdatedDates.push(sanitizeMedalliaValue(product.subscriptionUpdated));
    accumulator.subscriptionFrequencyDays.push(sanitizeMedalliaValue(product.subscriptionFrequencyDays));

    return accumulator;
};

const serializeSubscriptionProducts = (products: MedalliaSubscriptionProductAccumulator): PetsmartMedalliaProduct => ({
    ...serializeProducts(products),
    productTotalPrices: products.productTotalPrices.join(MEDALLIA_DELIMITER),
    subscriptionIds: products.subscriptionIds.join(MEDALLIA_DELIMITER),
    subscriptionTypes: products.subscriptionTypes.join(MEDALLIA_DELIMITER),
    subscriptionStartDates: products.subscriptionStartDates.join(MEDALLIA_DELIMITER),
    subscriptionCreatedDates: products.subscriptionCreatedDates.join(MEDALLIA_DELIMITER),
    subscriptionUpdatedDates: products.subscriptionUpdatedDates.join(MEDALLIA_DELIMITER),
    subscriptionFrequencyDays: products.subscriptionFrequencyDays.join(MEDALLIA_DELIMITER),
});

const buildSubscriptionProducts = (items: SubscriptionItem[]): PetsmartMedalliaProduct => {
    const products = items.reduce<MedalliaSubscriptionProductAccumulator>(
        (accumulator, item) => addSubscriptionProductToAccumulator(accumulator, buildSubscriptionProduct(item)),
        createSubscriptionProductAccumulator()
    );

    return serializeSubscriptionProducts(products);
};

const buildSubscriptionOrderInfo = (
    order: SubscriptionOrder,
    extras?: MedalliaContextExtras
): PetsmartMedalliaOrderInfo => {
    const orderInfo: PetsmartMedalliaOrderInfo = {};

    addIfAvailable(orderInfo, 'cartTotal', order.total);
    addIfAvailable(orderInfo, 'subTotal', order.subTotal);
    addIfAvailable(orderInfo, 'taxTotal', order.taxTotal);
    addIfAvailable(orderInfo, 'shippingTotal', order.shippingTotal);
    addIfAvailable(orderInfo, 'discountTotal', order.discountTotal);
    addIfAvailable(orderInfo, 'currencyCode', order.currencyCode);
    addIfAvailable(orderInfo, 'itemCount', order.items?.length);
    addIfAvailable(orderInfo, 'orderNumber', order.id);
    // `place` is the scheduled placement ("Order on") date shown in the subscription manager.
    addIfAvailable(orderInfo, 'orderDate', order.place);
    addIfAvailable(orderInfo, 'orderStatus', order.status);
    addIfAvailable(orderInfo, 'orderCreated', order.created);
    addIfAvailable(orderInfo, 'orderUpdated', order.updated);
    // Only the payment method label — never card number, holder, or token.
    addIfAvailable(orderInfo, 'paymentMethod', order.payment?.method);
    addIfAvailable(
        orderInfo,
        'optimizelyCampaigns',
        extras?.optimizelyCampaigns?.length ? JSON.stringify(extras.optimizelyCampaigns) : undefined
    );

    return orderInfo;
};

const buildSubscriptionInfo = (
    order: SubscriptionOrder,
    actionInfo?: PetsmartMedalliaSubscriptionActionInfo
): PetsmartMedalliaSubscriptionInfo => {
    const subscriptionInfo: PetsmartMedalliaSubscriptionInfo = {
        pageName: 'subscription_manager',
    };

    addIfAvailable(subscriptionInfo, 'subscriptionOrderId', order.id);
    addIfAvailable(subscriptionInfo, 'subscriptionId', actionInfo?.subscriptionId);
    addIfAvailable(subscriptionInfo, 'subscriptionFrequency', actionInfo?.subscriptionFrequency);
    addIfAvailable(subscriptionInfo, 'actionType', actionInfo?.actionType);
    addIfAvailable(subscriptionInfo, 'actionSuccess', actionInfo?.actionSuccess);
    addIfAvailable(subscriptionInfo, 'cancelReason', actionInfo?.cancelReason);

    return subscriptionInfo;
};

export const buildPetsmartMedalliaContextFromSubscription = (
    order: SubscriptionOrder,
    extras?: MedalliaContextExtras,
    actionInfo?: PetsmartMedalliaSubscriptionActionInfo
): PetsmartMedalliaContext => {
    return {
        userInfo: buildSubscriptionUserInfo(extras),
        products: buildSubscriptionProducts(order.items ?? []),
        orderInfo: buildSubscriptionOrderInfo(order, extras),
        subscriptionInfo: buildSubscriptionInfo(order, actionInfo),
    };
};

export const buildPetsmartMedalliaContextFromOrder = (
    order: OrderUI,
    extras?: MedalliaContextExtras,
    orderDate?: string
): PetsmartMedalliaContext => {
    const context = buildPetsmartMedalliaContextFromCartUI(order, extras);

    // order.summary.totalAmount arrives in cents from the raw API (loadOrderJsonById
    // bypasses CartProvider/applyFormatPriceFraction), so convert here before surfacing.
    const orderCartTotal =
        order.summary?.totalAmount == null
            ? undefined
            : toStringValue(order.summary.totalAmount / 10 ** (order.fractionDigits ?? 2));

    // Callers that need stable deduplication (e.g. MedalliaTrigger) should pass a
    // captured timestamp so that re-renders with unchanged data do not produce a
    // new serialized payload. Falls back to Date.now() for callers that don't care.
    const resolvedOrderDate = orderDate ?? String(Date.now());

    return {
        ...context,
        orderInfo: {
            ...context.orderInfo,
            ...(orderCartTotal ? { cartTotal: orderCartTotal } : {}),
            ...(order.orderNumber
                ? {
                      orderNumber: String(order.orderNumber),
                      transactionId: String(order.orderNumber),
                      orderDate: resolvedOrderDate,
                  }
                : {
                      orderDate: resolvedOrderDate,
                  }),
        },
    };
};
